import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, Button, CircularProgress, LinearProgress } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { getLatestAssessment } from "../services/assessmentService";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import AdminLayout from "./AdminLayout";

export default function OnboardingGate() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!user?.uid) return;
      const last = await getLatestAssessment(user.uid);
      // fetch fresh credits to avoid stale profile cache
      let total = userProfile?.totalCredits || 0;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) total = snap.data().totalCredits || 0;
      } catch (_) { }
      if (!active) return;
      setAssessment(last);
      setCredits(total);
      setLoading(false);
    }
    load();
    return () => { active = false; };
  }, [user]);

  if (userProfile?.role === "admin" || userProfile?.dashboardUnlocked) {
    const redirectPath =
      userProfile?.role === "admin"
        ? "/admin"
        : userProfile?.role === "startup"
          ? "/startup"
          : "/student";
    navigate(redirectPath, { replace: true });
    return null;
  }

  return (
    <AdminLayout title="Onboarding">
      <Box sx={{ p: 3 }}>
        <Card sx={{ maxWidth: 720, mx: "auto", borderTop: "6px solid #C1272D" }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#003366", mb: 1 }}>
              Welcome to the Qualification Center
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
              You need to qualify to unlock your dashboard. Complete the assessment or learn paths to earn credits.
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress sx={{ color: "#C1272D" }} />
              </Box>
            ) : (
              <Box>
                {!assessment && (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography sx={{ mb: 2 }}>Start with the Initial Assessment.</Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/assessment")}
                      sx={{
                        px: 4,
                        py: 1.2,
                        background: "linear-gradient(135deg,#003366,#004C99)",
                        "&:hover": { background: "linear-gradient(135deg,#00264d,#003d7a)" }
                      }}
                    >
                      Begin Assessment
                    </Button>
                  </Box>
                )}

                {assessment && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Latest Assessment Score
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, assessment.score))}
                      sx={{ height: 10, borderRadius: 2, mb: 1 }}
                    />
                    <Typography color="text.secondary">Score: {assessment.score}%</Typography>

                    <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        onClick={() => navigate("/assessment")}
                        sx={{
                          backgroundColor: "#C1272D",
                          "&:hover": { backgroundColor: "#A51F25" }
                        }}
                      >
                        Retake Assessment
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/student/learning/paths")}
                        sx={{
                          borderColor: "#003366",
                          color: "#003366",
                          "&:hover": { borderColor: "#00264d", color: "#00264d" }
                        }}
                      >
                        Go to Learning Paths
                      </Button>
                      {credits >= 480 && (
                        <Button
                          variant="contained"
                          onClick={() => navigate("/unlock")}
                          sx={{
                            ml: "auto",
                            background: "linear-gradient(135deg,#003366,#004C99)",
                            "&:hover": { background: "linear-gradient(135deg,#00264d,#003d7a)" }
                          }}
                        >
                          Unlock Dashboard
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
